import { Box, Stack } from '@chakra-ui/react';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DateString } from 'services/interfaces/contract';
import { Mixpanel } from 'services/mixpanel';
import { useFinancialContext } from '../../FinancialContext';
import CustomerLtvCard from '../molecules/CustomerLtvCard';
import CustomerOpenOrdersCard from '../molecules/CustomerOpenOrdersCard';
import ContractsHeader from '../molecules/ContractsHeader';
import ContractsListFilters, {
  contractFilter,
} from '../molecules/ContractsListFilters';
import ContractsTablePagination from '../molecules/ContractsTablePagination';
import ContractsTable from '../organisms/ContractsTable';

interface savedPageState {
  customerId: number | null;
  selectedPage: number | null;
  itensPerPage: number | null;
  periodicity: periodicityFilterState;
  status: 0 | 1 | null;
  includeDeleted: boolean | null;
  service: number | null;
  startDate: string | null;
  endDate: string | null;
}
export interface LoadingStt {
  stt: boolean;
  first: boolean;
}

type periodicityFilterState = 'yearly' | 'monthly' | null;

const ContractsPage = () => {
  const { isActive, loadContracts, contracts } = useFinancialContext();
  const { customer } = useCustomerContext();
  const [selectedPage, setSelectedPage] = useState(1);
  const [itensPerPage, setItensPerPage] = useState(20);
  const [loading, setLoading] = useState<LoadingStt>({
    stt: false,
    first: false,
  });
  const [periodicityFilter, setPeriodicityFilter] =
    useState<periodicityFilterState>(null);
  const [statusFilter, setStatusFilter] = useState<0 | 1 | null>(null);
  const [deletedFilter, setDeletedFilter] = useState<boolean | null>(true);
  const [serviceFilter, setServiceFilter] = useState<number | undefined>(
    undefined,
  );
  const [startDateFilter, setStartDateFilter] = useState<string | undefined>(
    undefined,
  );
  const [endDateFilter, setEndDateFilter] = useState<string | undefined>(
    undefined,
  );

  const totalItens = useRef(0);
  const lastStt = useRef<savedPageState>({
    customerId: null,
    periodicity: null,
    selectedPage: null,
    itensPerPage: null,
    status: null,
    includeDeleted: null,
    service: null,
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    Mixpanel.track('customers-financial-viewed-tab');
  }, []);

  const callLoadContracts = useCallback(
    async (displayLoadingStt?: boolean) => {
      if (loadContracts) {
        setLoading({
          stt: true,
          first: displayLoadingStt || false,
        });
        loadContracts({
          limit: itensPerPage,
          offset: itensPerPage * (selectedPage - 1),
          customer: customer.id,
          includeDeleted: deletedFilter ?? undefined,
          periodicity: periodicityFilter ? periodicityFilter : undefined,
          status: statusFilter ?? undefined,
          service: serviceFilter,
          startDate: startDateFilter as DateString | undefined,
          endDate: endDateFilter as DateString | undefined,
        })
          .then((response) => {
            lastStt.current = {
              customerId: customer.id,
              periodicity: periodicityFilter,
              selectedPage,
              itensPerPage,
              status: statusFilter,
              includeDeleted: deletedFilter,
              service: serviceFilter ?? null,
              startDate: startDateFilter ?? null,
              endDate: endDateFilter ?? null,
            };
            totalItens.current = response.total;
            setLoading({
              stt: false,
              first: displayLoadingStt || false,
            });
          })
          .catch((e) => {
            lastStt.current = {
              customerId: customer.id,
              periodicity: periodicityFilter,
              selectedPage,
              itensPerPage,
              status: statusFilter,
              includeDeleted: deletedFilter,
              service: serviceFilter ?? null,
              startDate: startDateFilter ?? null,
              endDate: endDateFilter ?? null,
            };
            setLoading({
              stt: false,
              first: displayLoadingStt || false,
            });
            console.log('Error fetching contracts:', e);
          });
      }
    },
    [
      customer.id,
      deletedFilter,
      itensPerPage,
      loadContracts,
      periodicityFilter,
      selectedPage,
      statusFilter,
      serviceFilter,
      startDateFilter,
      endDateFilter,
    ],
  );

  useEffect(() => {
    if (isActive) {
      const fetchContracts = () => {
        try {
          if (
            !loading.stt &&
            customer?.id &&
            (customer.id !== lastStt.current.customerId ||
              lastStt.current.periodicity !== periodicityFilter ||
              lastStt.current.status !== statusFilter ||
              lastStt.current.includeDeleted !== deletedFilter ||
              lastStt.current.selectedPage !== selectedPage ||
              lastStt.current.itensPerPage !== itensPerPage ||
              lastStt.current.service !== (serviceFilter ?? null) ||
              lastStt.current.startDate !== (startDateFilter ?? null) ||
              lastStt.current.endDate !== (endDateFilter ?? null))
          ) {
            let firstLoadStt = false;
            if (customer.id !== lastStt.current.customerId) {
              firstLoadStt = true;
            }
            callLoadContracts(firstLoadStt);
          }
        } catch (error) {
          setLoading({
            stt: false,
            first: false,
          });
          console.log('Error fetching contracts:', error);
        }
      };

      fetchContracts();
    }
  }, [
    customer.id,
    isActive,
    callLoadContracts,
    itensPerPage,
    selectedPage,
    periodicityFilter,
    loading.stt,
    statusFilter,
    deletedFilter,
    serviceFilter,
    startDateFilter,
    endDateFilter,
  ]);

  const handleFilterChange = useCallback((filter: contractFilter) => {
    if (filter.periodicity === 'monthly' || filter.periodicity === 'yearly') {
      setPeriodicityFilter(filter.periodicity);
    } else {
      setPeriodicityFilter(null);
    }

    if (filter.status !== null) {
      setStatusFilter(filter.status);
    } else {
      setStatusFilter(null);
    }

    if (filter.includeDeleted !== null) {
      setDeletedFilter(filter.includeDeleted);
    } else {
      setDeletedFilter(null);
    }

    setServiceFilter(filter.service);
    setStartDateFilter(filter.startDate);
    setEndDateFilter(filter.endDate);
    setSelectedPage(1);
  }, []);

  const handleChangePage = useCallback((selectedPage: number) => {
    setSelectedPage(selectedPage);
    window.scrollTo(0, 0);
  }, []);

  const handleChangePerPage = useCallback((itensPerPage: number) => {
    setSelectedPage(1);
    setItensPerPage(itensPerPage);
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Stack
        align="flex-start"
        padding="5"
        h="100%"
        bg="white"
        borderRadius={8}
        mb="4"
      >
        <Box mb="3" w="100%">
          <ContractsHeader contracts={contracts}></ContractsHeader>
        </Box>
        <Box mb="6" w="100%">
          <ContractsListFilters
            loading={loading}
            onFilterChange={handleFilterChange}
          ></ContractsListFilters>
        </Box>
        <ContractsTable
          loading={loading}
          contracts={contracts}
        ></ContractsTable>
        <ContractsTablePagination
          total={totalItens.current}
          selectedPage={selectedPage}
          perPage={itensPerPage}
          loading={loading}
          onPerPageChange={handleChangePerPage}
          onPageChange={handleChangePage}
        ></ContractsTablePagination>
      </Stack>
      <CustomerOpenOrdersCard />
      <CustomerLtvCard />
    </>
  );
};
export default ContractsPage;
